import apiClient from './apiClient';

/**
 * 宿主机管理服务 (User Version)
 *
 * 处理用户级别的容器和站点管理操作
 * 用户只能管理自己的资源
 *
 * 所有路由使用 /api/user/host/* 前缀
 */
class HostUserService {
  // ==========================================
  // 容器管理
  // ==========================================

  /**
   * 获取用户的容器列表
   */
  async getContainers() {
    try {
      const response = await apiClient.get('/api/user/host/containers');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 获取容器详情
   */
  async getContainer(id) {
    try {
      const response = await apiClient.get('/api/user/host/containers/get', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 启动容器
   */
  async startContainer(id) {
    try {
      const response = await apiClient.post('/api/user/host/containers/start', { id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 停止容器
   */
  async stopContainer(id) {
    try {
      const response = await apiClient.post('/api/user/host/containers/stop', { id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 重启容器
   */
  async restartContainer(id) {
    try {
      const response = await apiClient.post('/api/user/host/containers/restart', { id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 删除容器
   */
  async deleteContainer(id) {
    try {
      const response = await apiClient.delete('/api/user/host/containers/delete', {
        data: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 获取容器统计信息
   */
  async getContainerStats(id) {
    try {
      const response = await apiClient.get('/api/user/host/containers/stats', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // 站点管理
  // ==========================================

  /**
   * 获取站点列表
   */
  async getSites(containerId = null) {
    try {
      const params = containerId ? { container_id: containerId } : {};
      const response = await apiClient.get('/api/user/host/sites', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 获取站点详情
   */
  async getSite(id) {
    try {
      const response = await apiClient.get('/api/user/host/sites/get', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 创建站点
   */
  async createSite(data) {
    try {
      const response = await apiClient.post('/api/user/host/sites/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 更新站点
   */
  async updateSite(data) {
    try {
      const response = await apiClient.post('/api/user/host/sites/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 删除站点
   */
  async deleteSite(id) {
    try {
      const response = await apiClient.delete('/api/user/host/sites/delete', {
        data: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 启用站点
   */
  async enableSite(id) {
    try {
      const response = await apiClient.post('/api/user/host/sites/enable', { id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 禁用站点
   */
  async disableSite(id) {
    try {
      const response = await apiClient.post('/api/user/host/sites/disable', { id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // SSL证书管理
  // ==========================================

  /**
   * 获取SSL证书列表（仅用户自己的）
   */
  async getSslCertificates() {
    try {
      const response = await apiClient.get('/api/user/host/ssl/certificates');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 获取SSL证书详情
   * @param {number} id - 证书ID
   */
  async getSslCertificate(id) {
    try {
      const response = await apiClient.get('/api/user/host/ssl/certificates/get', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 创建自签名证书
   * @param {Object} data - { domain, valid_days, node_id }
   */
  async createSelfSignedCert(data) {
    try {
      const response = await apiClient.post('/api/user/host/ssl/self-signed', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 上传SSL证书
   * @param {Object} data - { domain, cert_content, key_content, node_id }
   */
  async uploadSslCert(data) {
    try {
      const response = await apiClient.post('/api/user/host/ssl/upload', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 使用系统SSL证书
   * @param {Object} data - { ssl_certificate_id, domain?, site_id?, node_id?, force_https?, hsts?, protocols?, ciphers? }
   */
  async useSystemCert(data) {
    try {
      const response = await apiClient.post('/api/user/host/ssl/use-system', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 删除SSL证书
   * @param {number} id - 证书ID
   */
  async deleteSslCertificate(id) {
    try {
      const response = await apiClient.delete('/api/user/host/ssl/certificates/delete', {
        data: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // 版本切换管理
  // ==========================================

  /**
   * 获取容器配置信息
   * @param {number} id - 容器ID
   */
  async getContainerConfig(id) {
    try {
      const response = await apiClient.get('/api/user/host/containers/config', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 切换PHP版本
   * @param {number} id - 容器ID
   * @param {string} version - PHP版本 (8.1, 8.2, 8.3)
   */
  async switchPhpVersion(id, version) {
    try {
      const response = await apiClient.post('/api/user/host/containers/switch-php-version', {
        id,
        version
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 切换Python版本
   * @param {number} id - 容器ID
   * @param {string} version - Python版本 (3.10, 3.11)
   */
  async switchPythonVersion(id, version) {
    try {
      const response = await apiClient.post('/api/user/host/containers/switch-python-version', {
        id,
        version
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 切换Node.js版本
   * @param {number} id - 容器ID
   * @param {string} version - Node.js版本 (18, 20)
   */
  async switchNodeVersion(id, version) {
    try {
      const response = await apiClient.post('/api/user/host/containers/switch-node-version', {
        id,
        version
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // 进程管理
  // ==========================================

  /**
   * 获取容器进程列表
   * @param {number} id - 容器ID
   */
  async getProcesses(id) {
    try {
      const response = await apiClient.get('/api/user/host/containers/processes', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 获取进程状态
   * @param {number} id - 容器ID
   * @param {string} process - 进程名称
   */
  async getProcessStatus(id, process) {
    try {
      const response = await apiClient.get('/api/user/host/containers/processes/status', {
        params: { id, process }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 启动进程
   * @param {number} id - 容器ID
   * @param {string} process - 进程名称
   */
  async startProcess(id, process) {
    try {
      const response = await apiClient.post('/api/user/host/containers/processes/start', {
        id,
        process
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 停止进程
   * @param {number} id - 容器ID
   * @param {string} process - 进程名称
   */
  async stopProcess(id, process) {
    try {
      const response = await apiClient.post('/api/user/host/containers/processes/stop', {
        id,
        process
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 重启进程
   * @param {number} id - 容器ID
   * @param {string} process - 进程名称
   */
  async restartProcess(id, process) {
    try {
      const response = await apiClient.post('/api/user/host/containers/processes/restart', {
        id,
        process
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Cron 任务管理
  // ==========================================

  /**
   * 获取Cron任务列表
   */
  async getCronJobs(containerId) {
    try {
      const response = await apiClient.get('/api/user/host/cron/list', {
        params: { container_id: containerId }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 获取单个Cron任务
   */
  async getCronJob(containerId, jobId) {
    try {
      const response = await apiClient.get('/api/user/host/cron/get', {
        params: { container_id: containerId, job_id: jobId }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 创建Cron任务
   */
  async createCronJob(data) {
    try {
      const response = await apiClient.post('/api/user/host/cron/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 更新Cron任务
   */
  async updateCronJob(data) {
    try {
      const response = await apiClient.post('/api/user/host/cron/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 删除Cron任务
   */
  async deleteCronJob(containerId, jobId) {
    try {
      const response = await apiClient.post('/api/user/host/cron/delete', {
        container_id: containerId,
        job_id: jobId
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 启用Cron任务
   */
  async enableCronJob(containerId, jobId) {
    try {
      const response = await apiClient.post('/api/user/host/cron/enable', {
        container_id: containerId,
        job_id: jobId
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 禁用Cron任务
   */
  async disableCronJob(containerId, jobId) {
    try {
      const response = await apiClient.post('/api/user/host/cron/disable', {
        container_id: containerId,
        job_id: jobId
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 立即运行Cron任务
   */
  async runCronJob(containerId, jobId) {
    try {
      const response = await apiClient.post('/api/user/host/cron/run', {
        container_id: containerId,
        job_id: jobId
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 获取Cron预设表达式
   */
  async getCronPresets(containerId) {
    try {
      const response = await apiClient.get('/api/user/host/cron/presets', {
        params: { container_id: containerId }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // 后台服务管理 (s6)
  // ==========================================

  /**
   * 获取后台服务列表
   */
  async getServices(containerId) {
    try {
      const response = await apiClient.get('/api/user/host/services/list', {
        params: { container_id: containerId }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 创建后台服务
   */
  async createService(data) {
    try {
      const response = await apiClient.post('/api/user/host/services/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 启动后台服务
   */
  async startService(containerId, serviceName) {
    try {
      const response = await apiClient.post('/api/user/host/services/start', {
        container_id: containerId,
        service_name: serviceName
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 停止后台服务
   */
  async stopService(containerId, serviceName) {
    try {
      const response = await apiClient.post('/api/user/host/services/stop', {
        container_id: containerId,
        service_name: serviceName
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 重启后台服务
   */
  async restartService(containerId, serviceName) {
    try {
      const response = await apiClient.post('/api/user/host/services/restart', {
        container_id: containerId,
        service_name: serviceName
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 删除后台服务
   */
  async deleteService(containerId, serviceName) {
    try {
      const response = await apiClient.post('/api/user/host/services/delete', {
        container_id: containerId,
        service_name: serviceName
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // 应用管理 (php-fpm, nodejs, python等)
  // ==========================================

  /**
   * 获取应用状态
   */
  async getAppStatus(containerId, type) {
    try {
      const response = await apiClient.get('/api/user/host/apps/status', {
        params: { container_id: containerId, type }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 创建应用
   */
  async createApp(data) {
    try {
      const response = await apiClient.post('/api/user/host/apps/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 启动应用
   */
  async startApp(containerId, type) {
    try {
      const response = await apiClient.post('/api/user/host/apps/start', {
        container_id: containerId,
        type
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 停止应用
   */
  async stopApp(containerId, type) {
    try {
      const response = await apiClient.post('/api/user/host/apps/stop', {
        container_id: containerId,
        type
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 重启应用
   */
  async restartApp(containerId, type) {
    try {
      const response = await apiClient.post('/api/user/host/apps/restart', {
        container_id: containerId,
        type
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 获取应用日志
   */
  async getAppLogs(containerId, type, lines = 100) {
    try {
      const response = await apiClient.get('/api/user/host/apps/logs', {
        params: { container_id: containerId, type, lines }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 删除应用
   */
  async deleteApp(containerId, type) {
    try {
      const response = await apiClient.post('/api/user/host/apps/delete', {
        container_id: containerId,
        type
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // PHP配置管理
  // ==========================================

  /**
   * 获取PHP配置
   */
  async getPhpConfig(containerId) {
    try {
      const response = await apiClient.get('/api/user/host/config/php', {
        params: { container_id: containerId }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 更新PHP配置
   */
  async updatePhpConfig(containerId, config) {
    try {
      const response = await apiClient.post('/api/user/host/config/php', {
        container_id: containerId,
        config
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 设置PHP ini值
   */
  async setPhpIniValue(containerId, key, value) {
    try {
      const response = await apiClient.post('/api/user/host/config/php/ini', {
        container_id: containerId,
        key,
        value
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 读取整份 php.ini
   */
  async getPhpIniRaw(containerId) {
    try {
      const response = await apiClient.get('/api/user/host/config/php/ini/raw', {
        params: { container_id: containerId }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 整份写回 php.ini
   */
  async setPhpIniRaw(containerId, content) {
    try {
      const response = await apiClient.post('/api/user/host/config/php/ini/raw', {
        container_id: containerId,
        content
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 列出 PHP 已加载扩展
   */
  async getPhpExtensions(containerId) {
    try {
      const response = await apiClient.get('/api/user/host/config/php/extensions', {
        params: { container_id: containerId }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 安装 PHP 扩展
   */
  async installPhpExtension(containerId, extension) {
    try {
      const response = await apiClient.post('/api/user/host/config/php/extensions/install', {
        container_id: containerId,
        extension
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 启用/禁用 PHP 扩展
   */
  async togglePhpExtension(containerId, extension, enable) {
    try {
      const response = await apiClient.post('/api/user/host/config/php/extensions/toggle', {
        container_id: containerId,
        extension,
        enable
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Nginx管理
  // ==========================================

  /**
   * 测试Nginx配置
   */
  async testNginx(containerId) {
    try {
      const response = await apiClient.get('/api/user/host/nginx/test', {
        params: { container_id: containerId }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 重载Nginx配置
   */
  async getTerminalTicket(containerId) {
    try {
      const response = await apiClient.post('/api/user/host/containers/terminal-ticket', { id: containerId });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async reloadNginx(containerId) {
    try {
      const response = await apiClient.post('/api/user/host/nginx/reload', {
        container_id: containerId
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // 容器维护
  // ==========================================

  /**
   * 修复容器权限
   */
  async fixPermissions(containerId) {
    try {
      const response = await apiClient.post('/api/user/host/containers/fix-permissions', {
        id: containerId
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 切换Golang版本
   */
  async switchGolangVersion(id, version) {
    try {
      const response = await apiClient.post('/api/user/host/containers/switch-golang-version', {
        id,
        version
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // 运行时版本管理
  // ==========================================

  /**
   * 获取容器内支持的运行时版本
   */
  async getRuntimeVersions(containerId) {
    try {
      const response = await apiClient.get('/api/user/host/runtime/versions', {
        params: { container_id: containerId }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }
}

export default new HostUserService();
